import type { User, UserRole } from '../types';
import { getUsers, updateUserLoginStatus, createLogEntry, getUserEventIds } from './storage';
import { clearActiveEvent } from './event';

const STORAGE_KEY = 'medical_camp_user';

export const saveUser = (user: User): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
};

export const getUser = (): User | null => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : null;
};

// Validate user session against Supabase to ensure account is still active
export const validateUserSession = async (): Promise<User | null> => {
  const localUser = getUser();
  if (!localUser) return null;

  try {
    const { supabase } = await import('./supabase');
    const { data: dbUser, error } = await supabase
      .from('app_users')
      .select('id, username, password, display_name, role, is_active, is_online, created_at, created_by, last_login_at, logout_timer')
      .eq('id', localUser.id)
      .maybeSingle();

    if (error) {
      console.warn('Session validation skipped (network error):', error.message);
      return localUser;
    }

    if (!dbUser || dbUser.is_active === false) {
      clearUser();
      return null;
    }

    // Resolve the events this user can access (master admin = all events)
    const eventIds = dbUser.role === 'master_admin'
      ? []
      : await getUserEventIds(localUser.id);

    const updatedUser: User = {
      id: dbUser.id,
      username: dbUser.username,
      displayName: dbUser.display_name,
      role: dbUser.role,
      createdAt: dbUser.created_at,
      createdBy: dbUser.created_by,
      isActive: dbUser.is_active,
      lastLoginAt: dbUser.last_login_at,
      isOnline: dbUser.is_online,
      logoutTimer: dbUser.logout_timer,
      eventIds,
    };
    saveUser(updatedUser);
    return updatedUser;
  } catch (error) {
    console.warn('Session validation error (falling back to local):', error);
    return localUser;
  }
};

// Alias for getUser — used across account pages
export const getCurrentUser = (): User | null => getUser();

export const clearUser = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const logout = async (): Promise<void> => {
  const user = getUser();
  
  if (user) {
    try {
      // Log the logout action BEFORE clearing user data
      await createLogEntry(user, 'Logout', 'User logged out');
      
      // Update online status in Supabase
      await updateUserLoginStatus(user.id, false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
  
  clearUser();
  clearActiveEvent();
};

export const hasRole = (user: User | null, allowedRoles: UserRole[]): boolean => {
  if (!user) return false;
  return allowedRoles.includes(user.role);
};

export const canAccessPatientRegistration = (user: User | null): boolean => {
  return hasRole(user, ['master_admin', 'admin', 'patient_registration']);
};

export const canAccessVitals = (user: User | null, vitalType?: string): boolean => {
  if (!user) return false;
  
  if (hasRole(user, ['master_admin', 'admin', 'all_vitals'])) return true;
  
  if (vitalType) {
    const roleMap: Record<string, UserRole[]> = {
      bp: ['bp', 'patient_monitor'],
      heart_rate: ['heart_rate', 'patient_monitor'],
      spo2: ['spo2', 'patient_monitor'],
      temperature: ['temperature'],
      rbs: ['rbs'],
      cholesterol: ['cholesterol'],
      height_weight: ['height_weight'],
      blood_grouping: ['blood_grouping'],
      colour_blindness: ['colour_blindness'],
      visual_acuity: ['visual_acuity'],
      ecg: ['patient_monitor'],
      body_fat: ['body_fat'],
    };
    return hasRole(user, roleMap[vitalType] || []);
  }
  
  return false;
};

export const canViewAllVitals = (user: User | null): boolean => {
  return hasRole(user, ['master_admin', 'admin', 'view_only', 'all_vitals', 'bp', 'heart_rate', 'spo2', 'temperature', 'rbs', 'cholesterol', 'height_weight', 'blood_grouping', 'colour_blindness', 'visual_acuity', 'patient_monitor', 'body_fat']);
};

export const canManageUsers = (user: User | null): boolean => {
  return hasRole(user, ['master_admin']);
};

export const canViewActivityLogs = (user: User | null): boolean => {
  return hasRole(user, ['master_admin', 'admin']);
};

export const canAccessAccounts = (user: User | null): boolean => {
  return hasRole(user, ['master_admin', 'admin']);
};

export const canCollectFee = (user: User | null): boolean => {
  return hasRole(user, ['master_admin', 'admin', 'cashier']);
};

export const requiresAutoLogout = (user: User | null): boolean => {
  return hasRole(user, ['master_admin', 'admin']);
};

export const isMasterAdmin = (user: User | null | undefined): boolean => {
  return !!user && user.role === 'master_admin';
};

/** Whether the user may access a given event. Master admin = all events. */
export const hasEventAccess = (user: User | null, eventId: string): boolean => {
  if (!user) return false;
  if (user.role === 'master_admin') return true;
  return (user.eventIds || []).includes(eventId);
};

export const isAdminOrMaster = (user: User | null | undefined): boolean => {
  return !!user && (user.role === 'admin' || user.role === 'master_admin');
};